import React, { useEffect, useState } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import DbService from "../services/DbService";
import { Chip } from "@mui/material";
import ActionsCell from "./actionsCell";
import { enqueueSnackbar } from "notistack";
import {
  createPrincipal,
  deletePrincipal,
  updatePrincipal,
} from "../services/principalService";
import { Principal } from "../types/Principal";
import { PrincipalForm } from "./forms/PrincipalForm";
import { useAdmin } from "../context/AdminContext";
import { DynamicTableListTemplate } from "./shared/DynamicTableListTemplate";
import { useHandleError } from "../hooks/useHandleError";
import EmailAvatar from "./EmailAvatar";

const PrincipalList: React.FC = () => {
  const handleError = useHandleError();

  const {
    principals,
    schoolsWithoutPrincipal,
    setPrincipalsToAdminProvider,
    refreshSchoolAndPrincipalList,
  } = useAdmin();

  console.log(schoolsWithoutPrincipal, "schoolsWithoutPrincipal");
  const [loading, setLoading] = useState<boolean>(true);

  const [selectedPrincipal, setSelectedPrincipal] = useState<
    Principal | undefined
  >(undefined);

  const [principalToDelete, setPrincipalToDelete] = useState<Principal | null>(
    null
  );

  const [hasSchoolsChanged, setHasSchoolsChanged] = useState<boolean>(false);

  const hasFetchedRef = React.useRef(false);

  const columns: GridColDef[] = [
    {
      field: "isActive",
      headerName: "Status",
      width: 90,
      renderCell: (params) => {
        const color = params.value === true ? "success" : "default";
        const text = params.value === true ? "ACTIVE" : "DISABLED";
        return (
          <Chip
            style={{ fontSize: ".7rem" }}
            label={text}
            size="small"
            color={color}
          />
        );
      },
    },
    { field: "name", headerName: "Name", width: 150 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "mobile", headerName: "Mobile", width: 200 },
    {
      field: "city",
      headerName: "City",
      width: 130,
      valueGetter: (params) => {
        return params.row?.city;
      },
    },
    {
      field: "country",
      headerName: "Country",
      width: 130,
      valueGetter: (params) => {
        return params.row?.country;
      },
    },
    { field: "schoolName", headerName: "School", width: 200 },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return;
        }
        const updatedBy = params.row["updatedBy"];
        const date = new Date(params.value);
        // Format the date using the user's local computer locale
        const formattedDate = date.toLocaleDateString("en-AU");

        return (
          <div
            style={{
              display: "flex",
              gap: ".25rem",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{formattedDate}</span>
            <EmailAvatar email={updatedBy}></EmailAvatar>
          </div>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row: Principal = params.row as Principal;

        return (
          <ActionsCell
            onEdit={() => handleEditPrincipal(row)}
            onDelete={() => handleDeletePrincipal(row)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    const fetchPrincipals = async () => {
      setLoading(true);
      try {
        const response = await DbService.getPrincipals();
        setPrincipalsToAdminProvider(response.data);
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetchedRef.current) {
      fetchPrincipals();
      hasFetchedRef.current = true;
    }
  }, [handleError, setPrincipalsToAdminProvider]);

  const handleCloseDialog = () => {
    if (hasSchoolsChanged) {
      refreshList();
    }
    setSelectedPrincipal(undefined);
  };

  const handleEditPrincipal = (row: Principal) => {
    setSelectedPrincipal(row);
  };

  const handlePrincipalSubmit = async (principalData: Principal) => {
    if (principalData._id) {
      handleUpdatePrincipal(principalData);
    } else {
      try {
        const response = await createPrincipal(principalData);
        enqueueSnackbar(response.message, {
          variant: "success",
        });

        setSelectedPrincipal(principalData);
        setHasSchoolsChanged(true);
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleUpdatePrincipal = async (principalData: Principal) => {
    if (!principalData._id) {
      return;
    }
    try {
      const response = await updatePrincipal(principalData._id, principalData);

      setSelectedPrincipal(response.data);

      enqueueSnackbar(response.message, {
        variant: "success",
      });
      setHasSchoolsChanged(true);
    } catch (error) {
      handleError(error);
    }
  };

  const handleDeletePrincipal = async (row: Principal) => {
    if (!row?._id) {
      enqueueSnackbar(`Id is missing from this ${row.name}`, {
        variant: "error",
      });
      return;
    }
    setPrincipalToDelete(row);
  };

  const handleDeletePrincipalConfirm = async (
    row: Principal | null | undefined
  ) => {
    if (row?._id) {
      try {
        const response = await deletePrincipal(row._id, row.email);
        enqueueSnackbar(response.message, {
          variant: "success",
        });
        refreshList();
      } catch (error) {
        handleError(error);
      } finally {
        setPrincipalToDelete(null);
      }
    }
  };

  const refreshList = async () => {
    setLoading(true);
    try {
      await refreshSchoolAndPrincipalList();
    } catch (err: any) {
      handleError(err);
    } finally {
      setLoading(false);
      setHasSchoolsChanged(false);
    }
  };

  return (
    <DynamicTableListTemplate<Principal>
      columns={columns}
      data={principals}
      loading={loading}
      selectedRow={selectedPrincipal}
      FormComponent={
        <PrincipalForm
          schools={schoolsWithoutPrincipal}
          selectedRow={selectedPrincipal}
          onSubmit={handlePrincipalSubmit}
        />
      }
      entityLabel="Principal"
      dialogTitle="Principal Entry Form"
      onClearSelectedRow={handleCloseDialog}
      onConfirmDelete={handleDeletePrincipalConfirm}
      entityToDelete={principalToDelete}
    />
  );
};

export default PrincipalList;
